import React, { useEffect, useMemo, useRef, useState } from "react";
import ModalB from "react-bootstrap/Modal";
import { Html5Qrcode } from "html5-qrcode";
import { FaCheck } from "react-icons/fa";
import api from "../../services/axios";
import "./modalCheckinQr.css";

const SCANNER_ELEMENT_ID = "checkin-qr-reader";

function obterParticipantes(item) {
  return (
    item?.agenda?.participantes ||
    item?.participantes ||
    []
  );
}

function resolverConvidado(item) {
  const participantes = obterParticipantes(item);
  if (!participantes.length) return null;

  // Na recepção do patrocinador, o item é a linha do patrocinador.
  const idPatrocinadorNaAgenda = item?.id_usuario;

  const porTipo = participantes.find(
    (p) => p?.usuario?.tipo === "convidado"
  );
  if (porTipo) return porTipo;

  if (idPatrocinadorNaAgenda != null) {
    const outro = participantes.find(
      (p) => Number(p.id_usuario ?? p.usuario?.id) !== Number(idPatrocinadorNaAgenda)
    );
    if (outro) return outro;
  }

  return participantes[0];
}

function encontrarParticipantePorId(participantes, idUsuario) {
  return (
    participantes.find(
      (p) => Number(p.id_usuario ?? p.usuario?.id) === Number(idUsuario)
    ) || null
  );
}

function ehModoMatchMeet(item, propModo) {
  if (propModo === "m_m") return true;
  const tipo = item?.agenda?.tipo || item?.tipo;
  return tipo === "m_m";
}

function obterIdAgenda(item) {
  return item?.id_agenda ?? item?.agenda?.id ?? item?.id;
}

function liberarTrilhasCamera() {
  const elemento = document.getElementById(SCANNER_ELEMENT_ID);
  if (!elemento) return;

  const videos = elemento.querySelectorAll("video");
  videos.forEach((video) => {
    const stream = video.srcObject;
    if (stream && typeof stream.getTracks === "function") {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {
          // ignora
        }
      });
    }
    video.srcObject = null;
    try {
      video.pause();
    } catch (_) {
      // ignora
    }
  });
}

function ModalCheckinQr(props) {
  const [tituloModal, setTituloModal] = useState(props.titulo || "Check-in");
  const [mensagemErro, setMensagemErro] = useState("");
  const [usuarioVerificado, setUsuarioVerificado] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [cameraErro, setCameraErro] = useState("");
  const [idLidoQr, setIdLidoQr] = useState(null);
  const [aguardandoConfirmacao, setAguardandoConfirmacao] = useState(false);
  const [qrCorresponde, setQrCorresponde] = useState(null);
  const [participanteLido, setParticipanteLido] = useState(null);

  const scannerRef = useRef(null);
  const processandoRef = useRef(false);
  const verificadoRef = useRef(false);
  const aguardandoConfirmacaoRef = useRef(false);
  const modalAbertoRef = useRef(false);
  const inicioIdRef = useRef(0);

  const modoMatchMeet = useMemo(
    () => ehModoMatchMeet(props.item, props.modo),
    [props.item, props.modo]
  );

  const participantes = useMemo(
    () => obterParticipantes(props.item),
    [props.item]
  );

  const convidado = useMemo(
    () => resolverConvidado(props.item),
    [props.item]
  );

  const nomeConvidado =
    participanteLido?.usuario?.nome ||
    convidado?.usuario?.nome ||
    "convidado";

  const idConvidadoEsperado = Number(
    convidado?.id_usuario ?? convidado?.usuario?.id
  );

  const pararScanner = async () => {
    inicioIdRef.current += 1;
    const scanner = scannerRef.current;
    scannerRef.current = null;

    if (scanner) {
      try {
        const estado = scanner.getState?.();
        if (estado === 2 || estado === 3) {
          await scanner.stop();
        }
      } catch (_) {
        // ignora erros ao parar
      }

      try {
        scanner.clear();
      } catch (_) {
        // ignora
      }
    }

    liberarTrilhasCamera();
  };

  const iniciarScanner = async () => {
    if (
      !modalAbertoRef.current ||
      verificadoRef.current ||
      aguardandoConfirmacaoRef.current
    ) {
      return;
    }

    await pararScanner();
    const inicioId = ++inicioIdRef.current;

    if (
      !modalAbertoRef.current ||
      verificadoRef.current ||
      aguardandoConfirmacaoRef.current ||
      inicioId !== inicioIdRef.current
    ) {
      return;
    }

    setCameraErro("");

    const elemento = document.getElementById(SCANNER_ELEMENT_ID);
    if (!elemento) return;

    try {
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const size = Math.min(viewfinderWidth, viewfinderHeight, 240);
            return { width: size, height: size };
          },
          aspectRatio: 1,
          disableFlip: false,
        },
        (decodedText) => {
          handleLeituraQr(decodedText);
        },
        () => {
          // frame sem QR — ignorar
        }
      );

      // Modal fechou (ou outra tentativa iniciou) durante o start — libera a câmera
      if (
        !modalAbertoRef.current ||
        verificadoRef.current ||
        aguardandoConfirmacaoRef.current ||
        inicioId !== inicioIdRef.current
      ) {
        await pararScanner();
      }
    } catch (err) {
      if (inicioId !== inicioIdRef.current || !modalAbertoRef.current) {
        liberarTrilhasCamera();
        return;
      }
      console.error("Erro ao iniciar câmera:", err);
      setCameraErro(
        "Não foi possível acessar a câmera. Verifique as permissões do navegador."
      );
    }
  };

  const validarIds = (idQr, idConvidado) => {
    if (!Number.isFinite(idQr) || idQr <= 0) {
      return {
        ok: false,
        mensagem: "QR Code inválido. Use o crachá do convidado.",
      };
    }

    if (!Number.isFinite(idConvidado) || idConvidado <= 0) {
      return {
        ok: false,
        mensagem: "Não foi possível identificar o convidado da reunião.",
      };
    }

    if (Number(idQr) !== Number(idConvidado)) {
      return {
        ok: false,
        mensagem:
          "O QR Code lido não pertence ao convidado desta reunião.",
      };
    }

    return { ok: true, mensagem: "" };
  };

  const handleLeituraQr = async (textoQr) => {
    if (
      processandoRef.current ||
      verificadoRef.current ||
      aguardandoConfirmacaoRef.current
    ) {
      return;
    }

    const idLido = Number(String(textoQr).trim());
    if (!Number.isFinite(idLido) || idLido <= 0) {
      setMensagemErro("QR Code inválido. Use o crachá do convidado.");
      return;
    }

    const validacao = validarIds(idLido, idConvidadoEsperado);

    processandoRef.current = true;
    aguardandoConfirmacaoRef.current = true;
    setMensagemErro(validacao.ok ? "" : validacao.mensagem);
    setQrCorresponde(validacao.ok);
    setParticipanteLido(
      validacao.ok
        ? convidado || encontrarParticipantePorId(participantes, idLido)
        : null
    );
    await pararScanner();
    setIdLidoQr(idLido);
    setAguardandoConfirmacao(true);
    processandoRef.current = false;
  };

  const cancelarConfirmacao = () => {
    aguardandoConfirmacaoRef.current = false;
    setAguardandoConfirmacao(false);
    setIdLidoQr(null);
    setParticipanteLido(null);
    setQrCorresponde(null);
    setMensagemErro("");
    setProcessando(false);
    processandoRef.current = false;
    requestAnimationFrame(() => {
      setTimeout(() => {
        iniciarScanner();
      }, 80);
    });
  };

  const confirmarCheckin = async () => {
    if (!idLidoQr || processandoRef.current || verificadoRef.current) return;

    const validacao = validarIds(idLidoQr, idConvidadoEsperado);
    setQrCorresponde(validacao.ok);

    if (!validacao.ok) {
      setMensagemErro(validacao.mensagem);
      return;
    }

    const idAgenda = obterIdAgenda(props.item);
    if (!idAgenda) {
      setMensagemErro("Não foi possível identificar a reunião.");
      setQrCorresponde(false);
      return;
    }

    processandoRef.current = true;
    setProcessando(true);
    setMensagemErro("");

    try {
      const resp = await api.post("/reunioes-valida-reuniao-qr", {
        id_usuario_qr: Number(idLidoQr),
        id_agenda: Number(idAgenda),
        id_usuario_convidado: Number(idConvidadoEsperado),
      });

      if (resp.data.reuniao) {
        verificadoRef.current = true;
        aguardandoConfirmacaoRef.current = false;
        setAguardandoConfirmacao(false);
        setUsuarioVerificado(true);
        setMensagemErro("");
        props.onCheckinConfirmado?.();
      } else {
        setMensagemErro(
          resp.data.mensagem || "Não foi possível confirmar o check-in."
        );
        setQrCorresponde(false);
        processandoRef.current = false;
        setProcessando(false);
      }
    } catch (err) {
      setMensagemErro(
        err?.response?.data?.mensagem || "Erro ao validar o QR Code."
      );
      setQrCorresponde(false);
      processandoRef.current = false;
      setProcessando(false);
    }
  };

  useEffect(() => {
    setTituloModal(props.titulo || "Check-in");
  }, [props.titulo]);

  useEffect(() => {
    modalAbertoRef.current = Boolean(props.show);

    if (props.show) {
      return () => {
        modalAbertoRef.current = false;
        pararScanner();
      };
    }

    pararScanner();
    setMensagemErro("");
    setUsuarioVerificado(false);
    setProcessando(false);
    setCameraErro("");
    setIdLidoQr(null);
    setParticipanteLido(null);
    setQrCorresponde(null);
    setAguardandoConfirmacao(false);
    processandoRef.current = false;
    verificadoRef.current = false;
    aguardandoConfirmacaoRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  const handleModal = async () => {
    modalAbertoRef.current = false;
    await pararScanner();
    props.toggleModal();
  };

  const renderCorpo = () => {
    if (usuarioVerificado) {
      return (
        <>
          <div className="checkin-qr-sucesso">
            <FaCheck />
            <div>Presença Confirmada!</div>
            <div className="checkin-qr-sucesso-nome">{nomeConvidado}</div>
          </div>
          <div className="area-botao-modal">
            <button className="btn-header-options" onClick={handleModal}>
              Fechar
            </button>
          </div>
        </>
      );
    }

    if (aguardandoConfirmacao) {
      return (
        <div className="checkin-qr-confirmacao">
          {qrCorresponde === false ? (
            <>
              <p className="checkin-qr-erro checkin-qr-erro--destaque">
                {mensagemErro ||
                  "O QR Code lido não pertence ao convidado desta reunião."}
              </p>
              <div className="checkin-qr-confirmacao-acoes">
                <button
                  type="button"
                  className="checkin-qr-btn checkin-qr-btn--primario"
                  onClick={cancelarConfirmacao}
                  disabled={processando}
                >
                  Ler novamente
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="checkin-qr-confirmacao-titulo">Confirmar check-in?</p>
              <p className="checkin-qr-confirmacao-texto">
                <span className="checkin-qr-confirmacao-detalhe">
                  QR Code lido para
                </span>
                <span className="checkin-qr-confirmacao-nome">
                  {nomeConvidado}
                </span>
                <span className="checkin-qr-confirmacao-detalhe">
                  Deseja confirmar a presença nesta reunião?
                </span>
              </p>

              {mensagemErro ? (
                <div className="checkin-qr-erro checkin-qr-erro--destaque">
                  {mensagemErro}
                </div>
              ) : null}

              <div className="checkin-qr-confirmacao-acoes">
                <button
                  type="button"
                  className="checkin-qr-btn checkin-qr-btn--secundario"
                  onClick={cancelarConfirmacao}
                  disabled={processando}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="checkin-qr-btn checkin-qr-btn--primario"
                  onClick={confirmarCheckin}
                  disabled={processando}
                >
                  {processando ? "Confirmando…" : "Confirmar check-in"}
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <>
        <p className="checkin-qr-instrucao">
          Aponte a câmera para o QR Code do crachá de{" "}
          <strong>{nomeConvidado}</strong>
          {modoMatchMeet ? " nesta reunião M&M" : ""}
        </p>

        {cameraErro ? (
          <div className="checkin-qr-erro">{cameraErro}</div>
        ) : null}

        {mensagemErro ? (
          <div className="checkin-qr-erro">{mensagemErro}</div>
        ) : null}
      </>
    );
  };

  return (
    <ModalB
      show={props.show}
      onHide={handleModal}
      dialogClassName="modal-checkin-qr-dialog"
      onEnter={() => {
        modalAbertoRef.current = true;
      }}
      onEntered={() => {
        if (
          modalAbertoRef.current &&
          !aguardandoConfirmacaoRef.current &&
          !verificadoRef.current
        ) {
          iniciarScanner();
        }
      }}
      onExit={() => {
        modalAbertoRef.current = false;
        pararScanner();
      }}
      onExited={() => {
        modalAbertoRef.current = false;
        pararScanner();
      }}
      backdrop="static"
      centered
    >
      <ModalB.Header closeButton>
        <ModalB.Title className="titulo-modal">{tituloModal}</ModalB.Title>
      </ModalB.Header>
      <ModalB.Body>
        <div className="area-modal-validacao modal-checkin-qr">
          <div className="checkin-qr-cabecalho-conteudo">
            <div className="label-agenda-modal checkin-qr-nome-convidado">
              {nomeConvidado}
            </div>
          </div>

          <div className="area-input-modal">
            <div
              id={SCANNER_ELEMENT_ID}
              className={`checkin-qr-reader${
                aguardandoConfirmacao || usuarioVerificado
                  ? " checkin-qr-reader--oculto"
                  : ""
              }`}
            />
            {renderCorpo()}
          </div>
        </div>
      </ModalB.Body>
    </ModalB>
  );
}

export default ModalCheckinQr;
